import {useState, ChangeEvent, useEffect} from 'react';
import {Box, IconButton, Typography} from '@mui/material';
import {t} from 'i18next';
import ArrModal from '../../../../../common/arr-modal/ArrModal';
import {useNavigate} from 'react-router-dom';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import ArrTextField from '../../../../../common/arr-text-field/ArrTextField';
import ArrTooltip from '../../../../../common/arr-tooltip/ArrTooltip';
import ArrTooltipWrapper from '../../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrLoadingSpinner from '../../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {ReactComponent as InfoIcon} from '../../../../../../../src/assets/images/info-icon.svg';
import './NewJobConfirmationModal.scss';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {RootState} from '../../../../../../redux/store';
import {
  resetEditMode,
  resetFormData,
  resetProtocol,
  initializeJobCreateProtocol,
  setCloneJobName,
  setDefineCriteria,
  showToast,
  syncProtocolToFormData,
} from '../../../../../../redux/slice/aiReviewSliceV2';
import {
  useCreateAIReviewJob,
  useSubmitAIReviewJob,
  useUpdateAIReviewJob,
} from '../../../../../../hooks/ai/review/useReview';
import {ToastType} from '../../../../../../shared/constants';
import {CRITERIA} from '../issue-coding-type-header/IssueCodingTypeHeader';
import {
  AI_REVIEW_JOB_STATUS,
  AI_REVIEW_JOB_TYPE,
} from '../../../constant/AiReviewConstants';
import {AIReviewJobStatus} from '../../../../../../api/types/aiReview.type';
import {
  buildCreateJobPayload,
  buildUpdateJobPayload,
} from '../buildCreateJobPayload';

interface NewJobConfirmationModalProps {
  showNewJobConfirmationModal: boolean;
  onCloseNewJobConfirmationModal: () => void;
}

const TruncatedConfirmationValue = ({value}: {value: string}) => (
  <Box className="list-value-wrap">
    <ArrTooltip
      content={value}
      trigger="hover"
      focusableWrapper
      hideInfoIconOnEmptyChildren
    >
      <Typography
        component="dd"
        className="typo-text label1_size list-value list-value--truncate"
        data-testid="job-list-item-label"
      >
        {value}
      </Typography>
    </ArrTooltip>
  </Box>
);

type ConfirmationDisplayValues = {
  jobName: string;
  jobType: string;
  jobGroup: string | number;
  sourceSelection: string;
  totalDocuments: string;
};

const PLACEHOLDER_VALUE = '---';

const buildConfirmationDisplayValues = (
  formData: RootState['aiReviewV2']['formData'],
  jobTypeLabel: string
): ConfirmationDisplayValues => ({
  jobName: formData.jobDetail?.jobName || PLACEHOLDER_VALUE,
  jobType: jobTypeLabel,
  jobGroup: formData.jobDetail?.selectedGroup?.name || PLACEHOLDER_VALUE,
  sourceSelection: formData.sourceSelectionName || PLACEHOLDER_VALUE,
  totalDocuments:
    formData.totalDocs != null
      ? Number(formData.totalDocs).toLocaleString()
      : PLACEHOLDER_VALUE,
});

const NewJobConfirmationModal = ({
  showNewJobConfirmationModal,
  onCloseNewJobConfirmationModal,
}: NewJobConfirmationModalProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const store = useStore();
  const jobType = useSelector((state: RootState) => state.aiReviewV2.jobType);

  const formData = useSelector((state: RootState) => state.aiReviewV2.formData);
  const [confirmationText, setConfirmationText] = useState<string>('');
  const [pendingAction, setPendingAction] = useState<'draft' | 'submit' | null>(
    null
  );
  // Validation logic: Button is enabled only if text matches exactly
  const isSubmitDisabled = !/^i\s+understand$/i.test(confirmationText.trim());
  const {mutate: createAIReviewJob, isPending: isCreating} =
    useCreateAIReviewJob();
  const {mutate: submitAIReviewJob, isPending: isSubmittingJob} =
    useSubmitAIReviewJob();
  const {mutate: updateAIReviewJob, isPending: isUpdating} =
    useUpdateAIReviewJob();
  const isSaving =
    pendingAction != null || isCreating || isSubmittingJob || isUpdating;

  const jobTypeLabel =
    jobType === AI_REVIEW_JOB_TYPE.issueCoding
      ? t('ai.review.jobType.issueCoding')
      : t('ai.review.jobType.relevancy');

  const [displayValues, setDisplayValues] = useState<ConfirmationDisplayValues>(
    () => buildConfirmationDisplayValues(formData, jobTypeLabel)
  );

  useEffect(() => {
    if (!showNewJobConfirmationModal || isSaving) {
      return;
    }
    setDisplayValues(buildConfirmationDisplayValues(formData, jobTypeLabel));
  }, [formData, isSaving, jobTypeLabel, showNewJobConfirmationModal]);

  const handleUnderstandInput = (e: ChangeEvent<HTMLInputElement>) => {
    setConfirmationText(e.target.value);
  };
  const onConfirmationModalCancel = () => {
    if (isSaving) {
      return;
    }
    onCloseNewJobConfirmationModal();
  };

  const triggerToast = (status: AIReviewJobStatus) => {
    if (status === AI_REVIEW_JOB_STATUS.draft) {
      dispatch(
        showToast({
          title: t('ai.review.newJobDetail.draftToastTitle'),
          message: t('ai.review.newJobDetail.draftToastDesc', {
            value: displayValues.jobName,
          }),
          status: [ToastType.Success],
        })
      );
    } else if (jobType === AI_REVIEW_JOB_TYPE.issueCoding) {
      dispatch(
        showToast({
          title: t('ai.review.newJobDetail.createToastTitle'),
          message: t('ai.review.newJobDetail.createToastIssueCodeDesc'),
          status: [ToastType.Success],
        })
      );
    } else {
      dispatch(
        showToast({
          title: t('ai.review.newJobDetail.createToastTitle'),
          message: t('ai.review.newJobDetail.createToastAIRelevancyDesc'),
          status: [ToastType.Success],
        })
      );
    }
    onCloseNewJobConfirmationModal();
    navigate('/AI/Review');
    dispatch(setCloneJobName(''));
    dispatch(resetFormData());
    dispatch(resetProtocol());
    dispatch(initializeJobCreateProtocol());
    dispatch(resetEditMode());
    dispatch(setDefineCriteria(CRITERIA.MANUAL));
    setPendingAction(null);
  };

  const handleSaveOrSubmit = (status: AIReviewJobStatus) => {
    if (isSaving) {
      return;
    }

    setPendingAction(
      status === AI_REVIEW_JOB_STATUS.draft ? 'draft' : 'submit'
    );
    dispatch(syncProtocolToFormData());
    const state = store.getState() as RootState;
    const payload = buildCreateJobPayload(
      state.aiReviewV2.formData,
      state.aiReviewV2.jobType,
      status,
      state.aiReviewV2.protocol,
      state.aiReviewV2.initialProtocol
    );

    const mutationOptions = {
      onSuccess: (data: {errorMessage?: string}) => {
        if (data?.errorMessage) {
          setPendingAction(null);
          dispatch(
            showToast({
              title: t('common.error'),
              message: data.errorMessage,
              status: [ToastType.Error],
            })
          );
          return;
        }
        triggerToast(status);
      },
      onError: (error: {
        response?: {data?: {detail?: string}};
        message?: string;
      }) => {
        setPendingAction(null);
        dispatch(
          showToast({
            title: t('common.error'),
            message:
              error?.response?.data?.detail ??
              error?.message ??
              t('common.error'),
            status: [ToastType.Error],
          })
        );
      },
    };

    if (state.aiReviewV2.formData.jobId) {
      updateAIReviewJob(
        buildUpdateJobPayload(
          state.aiReviewV2.formData,
          state.aiReviewV2.jobType,
          status,
          state.aiReviewV2.protocol,
          state.aiReviewV2.initialProtocol
        ),
        mutationOptions
      );
      return;
    }

    if (status === AI_REVIEW_JOB_STATUS.draft) {
      createAIReviewJob(payload, mutationOptions);
      return;
    }

    submitAIReviewJob(payload, mutationOptions);
  };
  const onConfirmationModalSavesAsDraft = () => {
    handleSaveOrSubmit(AI_REVIEW_JOB_STATUS.draft);
  };

  const onConfirmationModalSubmit = () => {
    handleSaveOrSubmit(AI_REVIEW_JOB_STATUS.submitted);
  };
  const documentsInfo = () => (
    <Typography
      className="typo-text subhead5_size"
      data-testid="documents-tooltip-desc"
    >
      {t('ai.review.newJobDetail.confirmationModal.toolToopTitle')}
    </Typography>
  );
  // Frozen when the modal is opened / while the request is in flight so a
  // form reset cannot flash placeholder values before the modal closes.
  const jobData = [
    {
      label: t('ai.review.newJobDetail.confirmationModal.jobName'),
      value: displayValues.jobName,
      truncate: true,
    },
    {
      label: t('ai.review.newJobDetail.confirmationModal.jobType'),
      value: displayValues.jobType,
      truncate: false,
    },
    {
      label: t('ai.review.newJobDetail.confirmationModal.jobGroup'),
      value: displayValues.jobGroup,
      truncate: false,
    },
    {
      label: t('ai.review.newJobDetail.confirmationModal.sourceSelection'),
      value: displayValues.sourceSelection,
      truncate: false,
    },
  ];
  return (
    <>
      <ArrModal
        className="newJobConfirmationModel"
        showModal={showNewJobConfirmationModal}
        onClose={onConfirmationModalCancel}
        showClose={false}
        disableEscapeKeyDown={isSaving}
        title={t('ai.review.newJobDetail.confirmationModal.title')}
        disableFocusTitle
        dataTestId="new-job-confirmation-modal"
        buttons={[
          {
            id: 'modal-cancel-button',
            label: t('common.cancel'),
            onClick: onConfirmationModalCancel,
            variant: 'text',
            dataTestId: 'njc-modal-cancel-button',
            className: 'modal-cancel-button',
            disabled: isSaving,
          },
        ]}
      >
        <>
          <Box className="job-list" data-test="job-list-wrapper" component="dl">
            {jobData &&
              jobData.map((item) => (
                <Box
                  key={item.label}
                  className="list-item-box"
                  data-testid="job-list-box"
                >
                  <Typography
                    component="dt"
                    className="typo-text body2_size list-label"
                    data-testid="job-list-item-labels"
                  >
                    {item.label}
                  </Typography>
                  {item.truncate ? (
                    <TruncatedConfirmationValue value={String(item.value)} />
                  ) : (
                    <Typography
                      component="dd"
                      className="typo-text label1_size list-value"
                      data-testid="job-list-item-label"
                    >
                      {item.value}
                    </Typography>
                  )}
                </Box>
              ))}
          </Box>
          <Box
            className="total-documents-box"
            data-testid="total-documents-box"
          >
            <Box className="tooltip-box">
              <Typography
                className="typo-text subhead3_size"
                data-testid="total-documents-title"
                id="total-documents-label"
              >
                {t('ai.review.newJobDetail.confirmationModal.totalDocuments')}
              </Typography>
              <ArrTooltipWrapper
                title={t(
                  'ai.review.newJobDetail.confirmationModal.toolToopTitle'
                )}
                trigger={
                  <IconButton
                    type="button"
                    size="small"
                    className="info-icon-button"
                    aria-label={t(
                      'ai.review.newJobDetail.confirmationModal.toolToopTitle'
                    )}
                    data-testid="total-documents-info"
                  >
                    <InfoIcon
                      fontSize="small"
                      className="info-icon"
                      aria-hidden="true"
                    />
                  </IconButton>
                }
              >
                {documentsInfo()}
              </ArrTooltipWrapper>
            </Box>
            <Box>
              <Typography
                className="typo-text subhead3_size"
                data-testid="total-documents-value"
                aria-labelledby="total-documents-label"
              >
                {displayValues.totalDocuments}
              </Typography>
            </Box>
          </Box>

          <Box className="input-box" data-testid="input-box">
            <Typography
              className="confirmation-modal-description"
              data-testid="confirmation-modal-description"
              id="new-job-confirmation-modal-description"
            >
              {t('ai.review.newJobDetail.confirmationModal.Description')}
            </Typography>
            <ArrTextField
              className="understand-input"
              sizeVariant="medium"
              value={confirmationText}
              dataTestId="i-understand-input"
              placeholder={t(
                'ai.review.newJobDetail.confirmationModal.inputPlaceHolderText'
              )}
              aria-label={t(
                'ai.review.newJobDetail.confirmationModal.inputPlaceHolderText'
              )}
              aria-describedby="new-job-confirmation-modal-description"
              onChange={handleUnderstandInput}
              state={isSaving ? 'inactive' : 'enabled'}
            />
          </Box>
          <Box
            className="confirmation-modal-btn-container"
            data-testid="confirmation-modal-btn-container"
          >
            <ArrButton
              variant="secondary"
              size="large"
              disabled={isSaving}
              loading={pendingAction === 'draft'}
              aria-label={t(
                'ai.review.newJobDetail.confirmationModal.saveAsDraftBtn'
              )}
              dataTestId="modaldraft-btn"
              className="draft-btn"
              onClick={onConfirmationModalSavesAsDraft}
            >
              {t('ai.review.newJobDetail.confirmationModal.saveAsDraftBtn')}
            </ArrButton>
            <ArrButton
              variant="primary"
              size="large"
              aria-label={t('common.submit')}
              dataTestId="submit-btn"
              className="modal-submit-btn"
              aria-disabled={isSubmitDisabled || isSaving}
              isAriaDisabled={isSubmitDisabled || isSaving}
              disabled={isSubmitDisabled || isSaving}
              loading={pendingAction === 'submit'}
              onClick={onConfirmationModalSubmit}
            >
              {t('common.submit')}
            </ArrButton>
          </Box>
        </>
      </ArrModal>
      <ArrLoadingSpinner overlay isOpen={isSaving} show={isSaving} />
    </>
  );
};

export default NewJobConfirmationModal;
